/**
 * useDeviationData.js
 *
 * Data-fetching hook for the Deviation Dashboard.
 *
 * ── Architecture ─────────────────────────────────────────────────────────────
 *
 *  useDeviationRecords(modules, filters, sources)
 *    └─ One POST bi-graph/preview per selected module
 *       No category / sub_category / kpi filter — fetch ALL KPIs for each module
 *       so the dashboard can:
 *         a) derive taxonomy options (category/sub/kpi) from rows
 *         b) populate the KPI Trigger Reference panel with min/max from rows
 *
 * ── Why no static thresholds ─────────────────────────────────────────────────
 *  Trigger min/max are configured in the Trigger Module UI.
 *  On save → riu_reporting_question_target_data.
 *  Backend sync (internalSyncGraphData) reads trigger rows and writes
 *  min_trigger_value + max_trigger_value into riu_ai_dashboard_mapping.
 *  bi-graph/preview reads from riu_ai_dashboard_mapping and returns:
 *    row.min_trigger_value  — lower deviation threshold
 *    row.max_trigger_value  — upper deviation threshold
 *  Therefore, deviation.config.js does NOT contain min/max values.
 *  All threshold values come from the preview response rows.
 *
 * ── bi-graph/preview request ─────────────────────────────────────────────────
 *  {
 *    dataSourceType: "REPORTING",
 *    widgetConfig: {
 *      module: ["Energy"],
 *      filters: {
 *        financial_year:  ["FY 2024-25"],
 *        location_id:     ["1","2"],     // empty = all
 *        sublocation_id:  [],
 *        category:        [],            // intentionally empty — fetch all KPIs
 *        sub_category:    [],
 *        kpi:             [],
 *        displayPeriods:  ["Apr","May"], // empty = all periods
 *      },
 *      group_by: ["kpi","category","sub_category","location_id","displayPeriods","financial_year"],
 *      aggregate: { sum: "value" },
 *      aggregateSubLocations: true,    // follow ESG-style request shape
 *    }
 *  }
 *
 * ── bi-graph/preview response row ────────────────────────────────────────────
 *  {
 *    kpi:               "Grid Electricity",
 *    category:          "Electricity",
 *    sub_category:      "Grid Power",
 *    location_id:       "3",
 *    displayPeriods:    "Apr",
 *    financial_year:    "FY 2024-25",
 *    sum_value:         850,
 *    unit:              "kWh",
 *    min_trigger_value: 700,   ← from riu_ai_dashboard_mapping via backend sync
 *    max_trigger_value: 1000,  ← from riu_ai_dashboard_mapping via backend sync
 *  }
 *
 * ── Deviation status computation ─────────────────────────────────────────────
 *  Computed client-side from the row's own min_trigger_value / max_trigger_value.
 *  If thresholds are not configured (null), status defaults to "Normal".
 *
 * ── What is NOT done here ─────────────────────────────────────────────────────
 *  ✗  bi-deviation/records   (invented endpoint — does not exist)
 *  ✗  bi-deviation/thresholds (removed — thresholds come from preview rows)
 *  ✗  Static STATIC_TAXONOMY  (removed — no fallback thresholds needed)
 *  ✗  category/sub_category/kpi in API filters (taxonomy is derived from rows)
 *  ✗  alertStatus in API call  (applied client-side in the dashboard)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config.json";
import deviationConfig from "./config/deviation.config";

// ─────────────────────────────────────────────────────────────────────────────
// RAW API CALL  — identical to usageData.js
// ─────────────────────────────────────────────────────────────────────────────
const fetchPreview = async (payload) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/preview`,
      {},
      payload,
      "POST",
      false,
      false
    );
    if (isSuccess && data?.data) return data.data;
    return [];
  } catch (err) {
    console.error("fetchPreview [deviation] error:", err);
    return [];
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// FILTER HELPERS  — mirrors applyFilters / injectFilters in usageData.js
// ─────────────────────────────────────────────────────────────────────────────

/** Canonical empty filter shape expected by bi-graph/preview. */
const blank = (overrides = {}) => ({
  financial_year: [],
  location_id:    [],
  sublocation_id: [],
  category:       [],
  sub_category:   [],
  kpi:            [],
  displayPeriods: [],
  ...overrides,
});

/**
 * Deep-clones blank() and stamps in active user selections.
 * Maps dashboard state names → API field names:
 *   financialYears → financial_year
 *   locationIds    → location_id
 *   months         → displayPeriods
 */
const applyFilters = (base, { financialYears, locationIds, months }) => {
  const f = blank(base);
  if (financialYears?.length) f.financial_year = financialYears;
  if (locationIds?.length)    f.location_id    = locationIds.map(String);
  if (months?.length)         f.displayPeriods = months;
  Object.keys(f).forEach((key) => {
    // Keep sublocation_id in the payload so the backend preview contract
    // matches ESG At a Glance. It remains empty for this dashboard.
    if (key === "sublocation_id") return;
    if (Array.isArray(f[key]) && f[key].length === 0) {
      delete f[key];
    }
  });
  return f;
};

// ─────────────────────────────────────────────────────────────────────────────
// HOOK: useDeviationRecords
//
// Fires ONE POST bi-graph/preview per selected module.
// No category/sub_category/kpi filters — intentionally wide fetch so the
// dashboard can derive all taxonomy options from the returned rows.
//
// Deviation status is computed client-side from row.min_trigger_value and
// row.max_trigger_value, which are written by the backend sync job.
//
// @param modules  string[]   — active modules from deviationConfig (e.g. ["Energy"])
// @param filters  {
//                   financialYears: string[],
//                   locationIds:    string[],   // source IDs from useSources()
//                   months:         string[],   // displayPeriods values
//                 }
//                 NOTE: category/sub/kpi/alertStatus are NOT passed to the API.
//                 They are applied client-side in the dashboard component.
// @param sources  object[]   — from useSources() for location_id → label mapping
//
// Returns:
//   records  — all rows for the selected modules with status computed.
//              alertStatus filtering is NOT applied here; the dashboard
//              component filters client-side so taxonomy options remain full.
//   loading  — boolean
//   reload   — manual trigger function
// ─────────────────────────────────────────────────────────────────────────────
export const useDeviationRecords = (
  modules  = [],
  filters  = {},
  sources  = [],
  enabled  = true
) => {
  const [records, setRecords] = useState([]);
  const [detailedRows, setDetailedRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [locationSummaryRows, setLocationSummaryRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const filtersRef            = useRef(null);

  // location_id (string) → human-readable display label
  const sourceMap = useMemo(() => {
    const m = {};
    sources.forEach((s) => {
      const label =
        s.unitCode ||
        `${s.location?.area ?? ""} ${s.location?.city ?? ""}`.trim() ||
        String(s.id);
      m[String(s.id)] = label;
    });
    return m;
  }, [sources]);

  const load = useCallback(async () => {
    if (!enabled || !modules.length) {
      setLoading(false);
      setRecords([]);
      setDetailedRows([]);
      setSummaryRows([]);
      setLocationSummaryRows([]);
      return;
    }

    setLoading(true);
    const allRecords = [];
    const summaryRowMap = new Map();
    const locationSummaryRowMap = new Map();

    const graphConfigs = deviationConfig.graphConfigs || {};

    // ── One bi-graph/preview call per module ─────────────────────────────────
    // category/sub_category/kpi left empty → fetch all KPIs for the module.
    // financial_year in group_by → comparison view can split by FY client-side.
    // category + sub_category in group_by → taxonomy options derivable from rows.
    await Promise.all(
      modules.map(async (mod) => {
        const moduleGraph = graphConfigs[mod];
        const summaryConfigs = moduleGraph?.dataConfigs?.length
          ? moduleGraph.dataConfigs
          : moduleGraph?.widgetConfig
            ? [{ widgetConfig: moduleGraph.widgetConfig, dataSourceType: moduleGraph.dataSourceType }]
            : [
                {
                  widgetConfig: {
                    module: [mod],
                    filters: blank(),
                    group_by: ["kpi"],
                    aggregate: { sum: "value" },
                    aggregateSubLocations: true,
                  },
                },
              ];

        const summaryRowsPerConfig = await Promise.all(
          summaryConfigs.map(async (cfg) => {
            const widgetConfig = cfg.widgetConfig || {};
            const summaryPayload = {
              dataSourceType: cfg.dataSourceType || widgetConfig.dataSourceType || moduleGraph?.dataSourceType || "REPORTING",
              widgetConfig: {
                ...widgetConfig,
                module: widgetConfig.module?.length ? widgetConfig.module : [mod],
                group_by: Array.isArray(widgetConfig.group_by) ? [...widgetConfig.group_by] : [],
                filters: applyFilters(widgetConfig.filters || blank(), {
                  financialYears: filters.financialYears,
                  locationIds: filters.locationIds,
                  months: filters.months,
                }),
              },
            };

            return fetchPreview(summaryPayload);
          })
        );

        const locationSummaryRowsPerConfig = await Promise.all(
          summaryConfigs.map(async (cfg) => {
            const widgetConfig = cfg.widgetConfig || {};
            const groupBy = Array.isArray(widgetConfig.group_by) ? [...widgetConfig.group_by] : [];
            if (!groupBy.includes("location_id")) groupBy.push("location_id");

            const summaryPayload = {
              dataSourceType: cfg.dataSourceType || widgetConfig.dataSourceType || moduleGraph?.dataSourceType || "REPORTING",
              widgetConfig: {
                ...widgetConfig,
                module: widgetConfig.module?.length ? widgetConfig.module : [mod],
                group_by: groupBy,
                filters: applyFilters(widgetConfig.filters || blank(), {
                  financialYears: filters.financialYears,
                  locationIds: filters.locationIds,
                  months: filters.months,
                }),
              },
            };

            return fetchPreview(summaryPayload);
          })
        );

        summaryRowsPerConfig.flat().forEach((row) => {
          const actual = Number(row.sum_value ?? row.value) || 0;
          const minRaw = row.min_trigger_value ?? "";
          const maxRaw = row.max_trigger_value ?? "";
          const minVal = row.min_trigger_value != null ? Number(row.min_trigger_value) : null;
          const maxVal = row.max_trigger_value != null ? Number(row.max_trigger_value) : null;
          const hasThresholds = minVal !== null && maxVal !== null;

          let status = "No Threshold";
          if (hasThresholds) {
            status = "Normal";
            if (actual > maxVal) status = "Above Max";
            else if (actual < minVal) status = "Below Min";
          }

          const normalizedRow = {
            module: mod,
            fy: row.financial_year ?? "",
            month: row.displayPeriods ?? row.display_periods ?? "",
            locId: String(row.location_id ?? ""),
            loc: sourceMap[String(row.location_id ?? "")] || String(row.location_id ?? ""),
            category: row.category ?? "",
            subCategory: row.sub_category ?? "",
            kpi: row.kpi ?? "",
            unit: row.unit ?? "",
            min: minVal,
            max: maxVal,
            actual,
            status,
            hasThreshold: hasThresholds,
          };

          const summaryKey = [
            normalizedRow.module,
            normalizedRow.fy,
            normalizedRow.month,
            normalizedRow.locId,
            normalizedRow.category,
            normalizedRow.subCategory,
            normalizedRow.kpi,
            normalizedRow.unit,
            minRaw,
            maxRaw,
            normalizedRow.actual,
          ].join("||");

          if (!summaryRowMap.has(summaryKey)) {
            summaryRowMap.set(summaryKey, normalizedRow);
          }
        });

        locationSummaryRowsPerConfig.flat().forEach((row) => {
          const actual = Number(row.sum_value ?? row.value) || 0;
          const minRaw = row.min_trigger_value ?? "";
          const maxRaw = row.max_trigger_value ?? "";
          const minVal = row.min_trigger_value != null ? Number(row.min_trigger_value) : null;
          const maxVal = row.max_trigger_value != null ? Number(row.max_trigger_value) : null;
          const hasThresholds = minVal !== null && maxVal !== null;

          let status = "No Threshold";
          if (hasThresholds) {
            status = "Normal";
            if (actual > maxVal) status = "Above Max";
            else if (actual < minVal) status = "Below Min";
          }

          const normalizedRow = {
            module: mod,
            fy: row.financial_year ?? "",
            month: row.displayPeriods ?? row.display_periods ?? "",
            locId: String(row.location_id ?? ""),
            loc: sourceMap[String(row.location_id ?? "")] || String(row.location_id ?? ""),
            category: row.category ?? "",
            subCategory: row.sub_category ?? "",
            kpi: row.kpi ?? "",
            unit: row.unit ?? "",
            min: minVal,
            max: maxVal,
            actual,
            status,
            hasThreshold: hasThresholds,
          };

          const summaryKey = [
            normalizedRow.module,
            normalizedRow.fy,
            normalizedRow.month,
            normalizedRow.locId,
            normalizedRow.category,
            normalizedRow.subCategory,
            normalizedRow.kpi,
            normalizedRow.unit,
            minRaw,
            maxRaw,
            normalizedRow.actual,
          ].join("||");

          if (!locationSummaryRowMap.has(summaryKey)) {
            locationSummaryRowMap.set(summaryKey, normalizedRow);
          }
        });

        const rawRowsPerConfig = await Promise.all(
          summaryConfigs.map(async (cfg) => {
            const widgetConfig = cfg.widgetConfig || {};
            return fetchPreview({
              dataSourceType: cfg.dataSourceType || widgetConfig.dataSourceType || moduleGraph?.dataSourceType || "REPORTING",
              widgetConfig: {
                ...widgetConfig,
                module: widgetConfig.module?.length ? widgetConfig.module : [mod],
                filters: applyFilters(widgetConfig.filters || blank(), {
                  financialYears: filters.financialYears,
                  locationIds: filters.locationIds,
                  months: filters.months,
                }),
                group_by: [
                  "kpi",
                  "category",
                  "sub_category",
                  "unit",
                  "min_trigger_value",
                  "max_trigger_value",
                  "location_id",
                  "displayPeriods",
                  "financial_year",
                ],
                aggregate: { sum: "value" },
                aggregateSubLocations: true,
              },
            });
          })
        );

        rawRowsPerConfig.flat().forEach((row) => {
          const actual = Number(row.sum_value ?? row.value) || 0;

          // ── Thresholds come from the row, written by internalSyncGraphData() ─
          const minVal = row.min_trigger_value != null
            ? Number(row.min_trigger_value)
            : null;
          const maxVal = row.max_trigger_value != null
            ? Number(row.max_trigger_value)
            : null;

          const locId = String(row.location_id ?? "");
          const loc   = sourceMap[locId] || locId;
          const fy    = row.financial_year ?? filters.financialYears?.[0] ?? "";
          const month = row.displayPeriods ?? row.display_periods ?? "";

          // ── Client-side deviation computation ─────────────────────────────
          // If thresholds are not configured for a KPI, exclude it from
          // Above / Below / Within summary counts by marking it separately.
          let status    = "No Threshold";
          let deviation = 0;

          const hasThresholds = minVal !== null && maxVal !== null;
          if (hasThresholds) {
            status = "Normal";
            if (actual > maxVal) {
              status    = "Above Max";
              deviation = actual - maxVal;          // positive = amount over limit
            } else if (actual > 0 && actual < minVal) {
              // Guard actual > 0: missing / zero readings treated as Normal.
              // Remove this guard if your domain considers zero a valid below-min.
              status    = "Below Min";
              deviation = minVal - actual;           // positive, for display
            }
          }

          allRecords.push({
            fy,
            month,
            locId,
            loc,
            kpi:         row.kpi         ?? "",
            unit:        row.unit         ?? "",
            module:      mod,
            category:    row.category     ?? "",
            subCategory: row.sub_category ?? "",
            // min/max stored for the KPI Trigger Reference panel
            min:         minVal,
            max:         maxVal,
            hasThreshold: hasThresholds,
            actual,
            status,
            alertSent:   hasThresholds && status !== "Normal",
            deviation,
            // alertTime is not returned by bi-graph/preview.
            // ⚠️ Map row.alertTime if the backend adds this field.
            alertTime: row.alertTime ?? null,
          });
        });

      })
    );

    setRecords(allRecords);
    setSummaryRows(Array.from(summaryRowMap.values()));
    setLocationSummaryRows(Array.from(locationSummaryRowMap.values()));
    setLoading(false);
  }, [modules, filters, sourceMap, enabled]);

  // Deep-compare via JSON — identical to filtersRef pattern in usageData.js
  useEffect(() => {
    const key = JSON.stringify({ modules, filters });
    if (filtersRef.current === key) return;
    filtersRef.current = key;
    load();
  }, [load, modules, filters]);

  return {
    records,
    detailedRows,
    summaryRows,
    locationSummaryRows,
    loading,
    reload: load,
  };
};
